import request from '../utils/request';
import type { Product, ProductCreateParams, ProductListResponse } from '../types/product';

export interface ListParams {
  page?: number;
  page_size?: number;
  search?: string;
  ordering?: string;
  has_logistics_package?: boolean;
}

export interface ListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Product[];
}

/**
 * 获取产品列表
 */
export const getProductList = async (params: {
  page?: number;
  page_size?: number;
  search?: string;
  creation_source?: string;
  created_by?: string;
  customer?: number | string;
}): Promise<ProductListResponse> => {
  const response = await request.get('/api/v1/products/', { params });
  return response.data;
};

/**
 * 获取产品详情
 */
export const getProductDetail = async (id: number): Promise<Product> => {
  const response = await request.get(`/api/v1/products/${id}/`);
  return response.data;
};

/**
 * 创建产品
 */
export const createProduct = async (data: ProductCreateParams): Promise<Product> => {
  const response = await request.post('/api/v1/products/', data);
  return response.data;
};

/**
 * 更新产品
 */
export const updateProduct = async (id: number, data: Partial<ProductCreateParams>): Promise<Product> => {
  const response = await request.patch(`/api/v1/products/${id}/`, data);
  return response.data;
};

/**
 * 删除产品
 */
export const deleteProduct = async (id: number): Promise<void> => {
  await request.delete(`/api/v1/products/${id}/`);
};

/**
 * 上传产品图片
 */
export const uploadProductPhoto = async (file: File): Promise<{ url: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await request.post('/api/v1/upload/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * 获取所有产品数据（用于批量下载）
 * 支持分页循环获取所有符合筛选条件的数据
 */
export const getAllProducts = async (filters: {
  search?: string;
  custom_code?: string;
  system_code?: string;
  sorting_codes?: string;
  has_logistics_package?: boolean;
  creation_source?: string;
  projects?: string;
  customer?: string;
  ordering?: string;
}): Promise<Product[]> => {
  const allProducts: Product[] = [];
  let page = 1;
  const pageSize = 100; // 每次获取100条，减少API调用次数
  
  while (true) {
    const params = {
      page,
      page_size: pageSize,
      ...filters
    };
    
    console.log(`📥 获取第${page}页产品数据，参数:`, params);
    
    try {
      const response = await getProductList(params);
      
      if (!response.results || response.results.length === 0) {
        console.log(`📥 第${page}页无数据，停止获取`);
        break;
      }
      
      allProducts.push(...response.results);
      console.log(`📥 第${page}页获取到${response.results.length}条数据，累计${allProducts.length}条`);
      
      // 如果没有下一页，停止获取
      if (!response.next) {
        console.log(`📥 已获取所有数据，共${allProducts.length}条`);
        break;
      }
      
      page++;
      
      // 防止无限循环，最多获取1000页
      if (page > 1000) {
        console.warn('⚠️ 达到最大页数限制，停止获取');
        break;
      }
      
    } catch (error) {
      console.error(`❌ 获取第${page}页产品数据失败:`, error);
      throw error;
    }
  }
  
  return allProducts;
}; 